import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHistory, 
  faLayerGroup, 
  faUniversity, 
  faMoneyBillWave 
} from '@fortawesome/free-solid-svg-icons';
import TransactionTable from './TransactionTable';

const TransactionHistory = ({ data, onRefresh, isLoading }) => {
  const [activeTab, setActiveTab] = useState('combined-tab');
  
  if (!data) return <div>Aucune donnée de transaction disponible</div>;
  
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };
  
  return (
    <div className="bloc">
      <h2>
        <FontAwesomeIcon icon={faHistory} /> Historique des transactions
      </h2>
      
      {/* Information sur les deux comptabilités */}
      <div style={{ marginBottom: '15px' }}>
        <div className="accounting-badge standard">
          <FontAwesomeIcon icon={faUniversity} /> Standard
        </div>
        <span style={{ fontSize: '11px', color: '#4f46e5' }}>
          Virements bancaires standards, remboursements de caution
        </span>
        
        <div className="accounting-badge v2" style={{ marginLeft: '15px' }}>
          <FontAwesomeIcon icon={faMoneyBillWave} /> V2
        </div>
        <span style={{ fontSize: '11px', color: '#0284c7' }}>
          Paiements en espèces, PayPal, autres méthodes manuelles
        </span>
      </div>
      
      {/* Onglets de navigation */}
      <div className="tab-container">
        <div className="tab-buttons">
          <button 
            className={`tab-button ${activeTab === 'combined-tab' ? 'active' : ''}`} 
            onClick={() => handleTabChange('combined-tab')}
            data-tab="combined-tab"
          >
            <FontAwesomeIcon icon={faLayerGroup} /> Combiné
          </button>
          <button 
            className={`tab-button ${activeTab === 'regular-tab' ? 'active' : ''}`} 
            onClick={() => handleTabChange('regular-tab')}
            data-tab="regular-tab"
          >
            <FontAwesomeIcon icon={faUniversity} /> Standard
          </button>
          <button 
            className={`tab-button ${activeTab === 'v2-tab' ? 'active' : ''}`} 
            onClick={() => handleTabChange('v2-tab')}
            data-tab="v2-tab"
          >
            <FontAwesomeIcon icon={faMoneyBillWave} /> V2
          </button>
        </div>
        
        {/* Contenu des onglets */}
        <div id="combined-tab" className={`tab-content ${activeTab === 'combined-tab' ? 'active' : ''}`}>
          {isLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <span>Mise à jour des données...</span>
            </div>
          )}
          <TransactionTable
            data={data}
            type="combined"
            soldeCourant={data.finance?.soldes?.courant}
            soldeCourantClass={getAmountClassString(data.finance?.soldes?.courant)}
            soldeCumulatif={data.soldeCumulatif?.total}
            soldeCumulatifClass={getAmountClassString(data.soldeCumulatif?.total)}
            onRefresh={onRefresh}
          />
        </div>
        
        <div id="regular-tab" className={`tab-content ${activeTab === 'regular-tab' ? 'active' : ''}`}>
          <div className="accounting-section">
            <div className="title">
              <span className="accounting-badge standard">
                <FontAwesomeIcon icon={faUniversity} /> Comptabilité Standard
              </span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                (Virements bancaires, transactions électroniques)
              </span>
            </div>
            
            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <span>Mise à jour des données...</span>
              </div>
            )}
            
            <TransactionTable
              data={data}
              type="standard"
              soldeFinal={data.finance?.soldes?.final}
              soldeFinalClass={getAmountClassString(data.finance?.soldes?.final)}
              regularSoldeClass={getAmountClassString(data.soldeCumulatif?.finalRegular)}
              onRefresh={onRefresh}
            />
          </div>
        </div>
        
        <div id="v2-tab" className={`tab-content ${activeTab === 'v2-tab' ? 'active' : ''}`}>
          <div className="accounting-section v2">
            <div className="title">
              <span className="accounting-badge v2">
                <FontAwesomeIcon icon={faMoneyBillWave} /> Comptabilité V2
              </span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                (Paiements en espèces, PayPal, autres méthodes manuelles)
              </span>
            </div>
            
            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <span>Mise à jour des données...</span>
              </div>
            )}
            
            <TransactionTable
              data={data}
              type="v2"
              soldeFinalV2={data.finance?.soldes?.finalV2}
              v2SoldeClass={getAmountClassString(data.finance?.soldes?.finalV2)}
              onRefresh={onRefresh}
            />
          </div>
        </div>
      </div>
      
      {/* Légende pour les comptabilités */}
      <div className="legend-box">
        <div style={{ marginBottom: '8px' }}>
          <strong>Légende des deux systèmes comptables:</strong>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <div>
            <span className="accounting-badge standard">
              <FontAwesomeIcon icon={faUniversity} /> Standard
            </span>
            <ul style={{ marginTop: '5px', marginBottom: '0', paddingLeft: '18px', fontSize: '10px' }}>
              <li>Virements bancaires standards</li>
              <li>Remboursements de caution</li>
              <li>Transactions électroniques</li>
            </ul>
          </div>
          <div>
            <span className="accounting-badge v2">
              <FontAwesomeIcon icon={faMoneyBillWave} /> V2
            </span>
            <ul style={{ marginTop: '5px', marginBottom: '0', paddingLeft: '18px', fontSize: '10px' }}>
              <li>Paiements en espèces</li>
              <li>Paiements PayPal</li>
              <li>Méthodes de paiement manuelles</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get CSS class based on amount
const getAmountClassString = (amount) => {
  if (typeof amount !== 'number') return '';
  return amount < 0 ? 'negative' : amount > 0 ? 'positive' : '';
};

export default TransactionHistory;
